import { useEffect, useState, useCallback } from 'react';
import { PackagePlus, Gift, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import api from '@/services/api';
import { ProductSuggestInput } from '@/components/shared/ProductSuggestInput';
import { formatDate } from '@/utils/dateFormat';

const INR = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DirectStockAdd = () => {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState('');
  const [isSample, setIsSample] = useState(true);
  const [cost, setCost] = useState('');
  const [batch, setBatch] = useState('');
  const [expiry, setExpiry] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try { const r = await api.get('/product-reports/direct-stock', { params: { limit: 100 } }); setHistory(r.data?.items || []); } catch { /* list is best-effort */ }
  }, []);

  useEffect(() => {
    api.get('/stores').then(r => setStores((r.data?.stores || r.data || []).filter(s => s.is_active !== false))).catch(() => {});
    loadHistory();
  }, [loadHistory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!storeId || !product || !(Number(qty) > 0) || notes.trim().length < 3) {
      toast.error('Select store, product, quantity and write a note (why this stock is being added)');
      return;
    }
    setSaving(true);
    try {
      const payload = { store_id: storeId, product_id: product.id, quantity: Number(qty), is_sample: isSample, notes: notes.trim(), batch_number: batch || null, expiry_date: expiry || null };
      if (!isSample && cost !== '') payload.cost_price = Number(cost);
      const r = await api.post('/product-reports/direct-stock', payload);
      toast.success(`${r.data.quantity} × ${r.data.product_name} added to ${r.data.store_code} (${r.data.reference_number})`);
      setProduct(null); setQty(''); setNotes(''); setBatch(''); setExpiry(''); setCost('');
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add stock');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="direct-stock-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><PackagePlus className="w-6 h-6 text-teal-600" /> Direct Stock to Store</h1>
        <p className="text-gray-500 text-sm">Add stock to a store without GRN or request — vendor samples, free pieces, promo stock. It becomes billable immediately and is deducted by sales like any other stock.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Card className="bg-white border-gray-200 xl:col-span-2">
          <CardHeader><CardTitle className="text-base">Add stock</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-xs text-gray-500">Store</Label>
                <Select value={storeId} onValueChange={setStoreId}>
                  <SelectTrigger data-testid="direct-stock-store"><SelectValue placeholder="Select store" /></SelectTrigger>
                  <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.code} — {s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Product</Label>
                <ProductSuggestInput value={product} onSelect={setProduct} testId="direct-stock-product" />
                {product && <p className="text-xs text-gray-500 mt-1">MRP {INR(product.mrp)} · cost {INR(product.cost_price)}</p>}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs text-gray-500">Quantity</Label><Input type="number" min="0.001" step="any" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="direct-stock-qty" /></div>
                <div><Label className="text-xs text-gray-500">Batch (optional)</Label><Input value={batch} onChange={(e) => setBatch(e.target.value)} placeholder={isSample ? 'SAMPLE' : ''} data-testid="direct-stock-batch" /></div>
                <div><Label className="text-xs text-gray-500">Expiry (optional)</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="direct-stock-expiry" /></div>
                {!isSample && <div><Label className="text-xs text-gray-500">Cost per unit</Label><Input type="number" min="0" step="any" value={cost} onChange={(e) => setCost(e.target.value)} placeholder={product ? String(product.cost_price ?? '') : ''} data-testid="direct-stock-cost" /></div>}
              </div>
              <div className={`flex items-start justify-between gap-3 rounded-lg border p-3 ${isSample ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex gap-2">
                  <Gift className={`w-5 h-5 mt-0.5 ${isSample ? 'text-teal-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">Mark as sample / free stock</p>
                    <p className="text-xs text-gray-600">Sold first and counted at ₹0 cost — 100% margin in Profit Analysis until the sample pieces run out; later GRN stock of the same product keeps its real cost.</p>
                  </div>
                </div>
                <Switch checked={isSample} onCheckedChange={setIsSample} data-testid="direct-stock-sample-switch" />
              </div>
              <div>
                <Label className="text-xs text-gray-500">Notes (required)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="e.g. 6 sample pcs given by Lotus vendor rep on 12 Jun" data-testid="direct-stock-notes" />
              </div>
              <Button type="submit" disabled={saving} className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="direct-stock-submit">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackagePlus className="w-4 h-4 mr-2" />} Add to store stock
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="bg-white border-gray-200 xl:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Recent direct additions</CardTitle>
            <Button variant="ghost" size="sm" onClick={loadHistory} data-testid="direct-stock-refresh"><RefreshCw className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent className="p-0">
            {history.length === 0 ? <div className="py-10 text-center text-gray-500 text-sm">Nothing added yet</div> : (
              <Table>
                <TableHeader><TableRow className="bg-gray-50"><TableHead>Date</TableHead><TableHead>Store</TableHead><TableHead>Product</TableHead><TableHead className="text-right">Added</TableHead><TableHead className="text-right">Left</TableHead><TableHead>Type</TableHead><TableHead>Notes</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
                <TableBody>
                  {history.map(h => (
                    <TableRow key={h.id} data-testid={`direct-stock-row-${h.reference_number}`}>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">{formatDate(h.created_at)}<p className="font-mono text-[10px] text-gray-400">{h.reference_number}</p></TableCell>
                      <TableCell className="text-sm">{h.store_code}</TableCell>
                      <TableCell className="text-sm"><p className="font-medium text-gray-800">{h.product_name}</p><p className="text-xs text-gray-500 font-mono">{h.sku}{h.expiry_date ? ` · exp ${h.expiry_date}` : ''}</p></TableCell>
                      <TableCell className="text-right font-semibold">{h.quantity}</TableCell>
                      <TableCell className={`text-right ${h.remaining_qty === 0 ? 'text-gray-400' : 'text-teal-700 font-semibold'}`}>{h.remaining_qty}</TableCell>
                      <TableCell>{h.is_sample ? <Badge className="bg-teal-100 text-teal-700 border-0">Sample</Badge> : <Badge variant="outline">Cost {INR(h.cost_price)}</Badge>}</TableCell>
                      <TableCell className="text-xs text-gray-600 max-w-[220px] truncate" title={h.notes}>{h.notes}</TableCell>
                      <TableCell className="text-xs text-gray-500">{h.created_by_name}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DirectStockAdd;
